/** Evaluate a single comparison against the current state. */
export function evaluateComparison(state, cmp) {
    if (!cmp || !cmp.variable)
        return false;
    const a = state[cmp.variable];
    const b = cmp.value;
    if (a === undefined)
        return false;
    switch (cmp.op) {
        case '==': return a === b;
        case '!=': return a !== b;
        case '<': return a < b;
        case '<=': return a <= b;
        case '>': return a > b;
        case '>=': return a >= b;
        default: return false;
    }
}
/**
 * Evaluate a condition against the current state. `null` and an empty
 * comparisons list are both treated as vacuously true. See SKILL.md
 * "implementation gotchas" for why this matters in chain contexts.
 */
export function evaluateCondition(state, cond) {
    if (cond === null || cond === undefined)
        return true;
    const cs = cond.comparisons || [];
    if (cs.length === 0)
        return true;
    if (cond.combinator === 'any')
        return cs.some(c => evaluateComparison(state, c));
    return cs.every(c => evaluateComparison(state, c));
}
/** Apply a sequence of effects to the state, returning a new state object. */
export function applyEffects(state, effects) {
    if (!effects || effects.length === 0)
        return state;
    const next = { ...state };
    for (const e of effects) {
        if (!e || !e.variable)
            continue;
        if (e.op === 'set') {
            next[e.variable] = e.value;
        }
        else if (e.op === 'add') {
            const cur = typeof next[e.variable] === 'number' ? next[e.variable] : 0;
            const inc = typeof e.value === 'number' ? e.value : 0;
            next[e.variable] = cur + inc;
        }
    }
    return next;
}
/**
 * Walk the variants chain top-to-bottom; return the first whose condition
 * matches (or which has `condition: null`). Validation should prevent the
 * no-match case, but we defensively return the last entry rather than crash.
 */
export function selectVariant(node, state) {
    const variants = node.variants || [];
    for (const v of variants) {
        if (v.condition === null || evaluateCondition(state, v.condition))
            return v;
    }
    return variants[variants.length - 1] || { id: '', condition: null, sceneText: '', media: [] };
}
/**
 * Walk a choice's feedbackVariants chain top-to-bottom; return the first
 * matching entry's text. Same shape as selectVariant. Text may be `""`
 * (means "no feedback shown for this state"); that's not an error.
 */
export function selectFeedback(choice, state) {
    const variants = choice.feedbackVariants || [];
    for (const v of variants) {
        if (v.condition === null || evaluateCondition(state, v.condition))
            return v.text || '';
    }
    const last = variants[variants.length - 1];
    return (last && last.text) || '';
}
/**
 * Walk a choice's destinations chain; return the first matching destination's id
 * (which may be `null` if the destination is unwired). Returns `null` if the
 * choice has no destinations (validation should prevent that).
 */
export function resolveDestination(choice, state) {
    const dests = choice.destinations || [];
    for (const d of dests) {
        if (d.condition === null || evaluateCondition(state, d.condition))
            return d.destinationId;
    }
    return null;
}
/** Filter a node's choices to those visible given the current state. */
export function filterVisibleChoices(node, state) {
    return (node.choices || []).filter(c => !c.visibleIf || evaluateCondition(state, c.visibleIf));
}
/** Build the initial state from a scenario's variable declarations. */
export function defaultStateFromVariables(variables) {
    const out = {};
    for (const [name, decl] of Object.entries(variables || {})) {
        out[name] = decl.default;
    }
    return out;
}
/**
 * Compute the starting state of a session for a scenario: variable defaults
 * with the start node's entry effects applied. This is what the runtime
 * records as `session_start.payload.initialState` (see the event-payload
 * schema in SKILL.md).
 */
export function initialStateForScenario(scenario) {
    const defaults = defaultStateFromVariables(scenario.variables || {});
    const startNode = scenario.nodes[scenario.startNodeId];
    if (!startNode)
        return defaults;
    return applyEffects(defaults, startNode.entryEffects);
}
//# sourceMappingURL=evaluator.js.map